import React, { useState, useEffect } from "react";
import {
  FaSearch,
  FaCalendarAlt,
  FaUser,
  FaTag,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaChartLine,
  FaClipboardList,
  FaUsers,
  FaComments,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import { analyticsService } from "../services/analyticsService";
import "../styles/blog-header.css";

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState("");

  // Add effect to track search terms when user searches
  useEffect(() => {
    // Only track search when user has actually searched something (not on initial render)
    if (searchTerm.trim().length > 2) {
      // Debounce tracking to avoid sending too many events
      const timer = setTimeout(() => {
        analyticsService.trackSearch(searchTerm);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [searchTerm]);

  // Sample blog posts data
  const blogPosts = [
    {
      id: 1,
      title: "Transforming Education Management with Technology in 2025",
      excerpt:
        "Discover how modern school management systems are revolutionizing administrative processes and improving student outcomes across educational institutions.",
      author: "Dr. Raman Sharma",
      date: "May 15, 2025",
      category: "Education Technology",
      image:
        "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tags: ["Education Management", "EdTech", "Digital Transformation"],
    },
    {
      id: 2,
      title:
        "10 Essential Features Every Coaching Institute Management System Needs",
      excerpt:
        "Looking to streamline your coaching center operations? Learn about the must-have features that can help you manage students, faculty, and courses efficiently.",
      author: "Priya Desai",
      date: "May 2, 2025",
      category: "Coaching Management",
      image:
        "https://images.unsplash.com/photo-1588072432836-e10032774350?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tags: ["Coaching Center", "Management Software", "Educational Tools"],
    },
    {
      id: 3,
      title:
        "How to Improve Student Attendance and Engagement with Digital Tools",
      excerpt:
        "Struggling with student absenteeism? Discover proven digital strategies that can boost attendance rates and enhance classroom participation in your institution.",
      author: "Anil Kumar",
      date: "April 18, 2025",
      category: "Student Management",
      image:
        "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tags: [
        "Attendance Tracking",
        "Student Engagement",
        "Educational Success",
      ],
    },
    {
      id: 4,
      title:
        "Streamlining Fee Collection: Modern Solutions for Educational Institutions",
      excerpt:
        "Managing finances can be challenging for schools and coaching centers. Learn how digital fee management systems can reduce administrative burden and improve cash flow.",
      author: "Meera Patel",
      date: "April 5, 2025",
      category: "Financial Management",
      image:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tags: ["Fee Management", "School Finance", "Digital Payments"],
    },
    {
      id: 5,
      title: "Building Effective Communication Between Parents and Teachers",
      excerpt:
        "Parental involvement is crucial for student success. Explore how modern communication tools in educational management systems can foster stronger parent-teacher relationships.",
      author: "Dr. Anjali Mehta",
      date: "March 22, 2025",
      category: "Communication",
      image:
        "https://images.unsplash.com/photo-1577896851231-70ef18881754?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tags: [
        "Parent Communication",
        "Teacher Collaboration",
        "Education Community",
      ],
    },
    {
      id: 6,
      title: "Data-Driven Decision Making in Education: A Comprehensive Guide",
      excerpt:
        "Harness the power of analytics to make informed decisions for your educational institution. Learn how to interpret student performance data and implement effective interventions.",
      author: "Prof. Suresh Venkat",
      date: "March 10, 2025",
      category: "Analytics",
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      tags: [
        "Education Analytics",
        "Performance Metrics",
        "Data-Driven Teaching",
      ],
    },
  ];

  // Create blog structured data for JSON-LD
  const blogStructuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    headline: "Education Management Blog - Pathshala Sanchalak",
    description:
      "Articles and insights about school management, coaching centers, and educational best practices",
    author: {
      "@type": "Organization",
      name: "Pathshala Sanchalak",
      url: "https://pathshalasanchalak.in",
    },
    publisher: {
      "@type": "Organization",
      name: "Pathshala Sanchalak",
      logo: {
        "@type": "ImageObject",
        url: "https://pathshalasanchalak.in/logo512.png",
      },
    },
    blogPost: blogPosts.map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      author: {
        "@type": "Person",
        name: post.author,
      },
      keywords: post.tags.join(", "),
      image: post.image,
    })),
  };

  // Filter blog posts based on search term
  const filteredPosts = blogPosts.filter(
    (post) =>
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      )
  );

  // Create array of unique categories
  const categories = [...new Set(blogPosts.map((post) => post.category))];

  return (
    <div className="blog-page container mx-auto px-4 py-12">
      <Helmet>
        <title>Education Management Blog - Pathshala Sanchalak</title>
        <meta
          name="description"
          content="Read the latest articles, tips and best practices for managing educational institutions effectively."
        />
        <meta
          name="keywords"
          content="education blog, school management tips, coaching center management, educational institution best practices"
        />
        <script type="application/ld+json">
          {JSON.stringify(blogStructuredData)}
        </script>
      </Helmet>

      <h1 className="text-4xl font-bold text-center mb-12">
        Education Management Resource Center
      </h1>

      <section className="blog-hero section-padding">
        <div className="container">
          <div
            className="section-header blog-section-header"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              padding: "30px 20px",
              borderRadius: "8px",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
            }}
          >
            <h2>Pathshala Sanchalak Knowledge Hub</h2>
            <p>
              Expert insights and guidance for educational institution
              management
            </p>
          </div>
          <div className="blog-search">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search for educational resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search educational resources"
              />
              <button className="search-btn" aria-label="Search">
                <FaSearch />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="blog-content section-padding">
        <div className="container">
          <div className="blog-grid">
            <div className="blog-posts">
              {filteredPosts.length > 0 ? (
                filteredPosts.map((post) => (
                  <div className="blog-card" key={post.id}>
                    <div className="blog-image">
                      <img src={post.image} alt={post.title} />
                      <span className="blog-category">
                        {post.category === "Education Technology" && (
                          <FaGraduationCap />
                        )}
                        {post.category === "Coaching Management" && (
                          <FaChalkboardTeacher />
                        )}
                        {post.category === "Student Management" && <FaUsers />}
                        {post.category === "Financial Management" && (
                          <FaChartLine />
                        )}
                        {post.category === "Communication" && <FaComments />}
                        {post.category === "Analytics" && (
                          <FaClipboardList />
                        )}{" "}
                        {post.category}
                      </span>
                    </div>
                    <div className="blog-details">
                      <h3>{post.title}</h3>
                      <div className="blog-meta">
                        <span>
                          <FaUser /> {post.author}
                        </span>
                        <span>
                          <FaCalendarAlt /> {post.date}
                        </span>
                      </div>
                      <p>{post.excerpt}</p>
                      <div className="blog-tags">
                        {post.tags.map((tag, index) => (
                          <span key={index} className="blog-tag">
                            <FaTag /> {tag}
                          </span>
                        ))}
                      </div>
                      <a 
                        href={`/blog/${post.id}`} 
                        className="btn btn-primary"
                        onClick={() => analyticsService.trackEvent(
                          'Blog',
                          'Post View',
                          `Title: ${post.title}`,
                          1
                        )}
                      >
                        Read More
                      </a>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <h3>No resources found</h3>
                  <p>
                    Try searching with different keywords related to education
                    management
                  </p>
                </div>
              )}
            </div>

            <div className="blog-sidebar">
              <div className="sidebar-widget categories-widget">
                <h3>Resource Categories</h3>
                <ul>
                  {categories.map((category, index) => (
                    <li key={index}>
                      <a href={`/blog/category/${category.toLowerCase()}`}>
                        {category === "Education Technology" && (
                          <FaGraduationCap />
                        )}
                        {category === "Coaching Management" && (
                          <FaChalkboardTeacher />
                        )}
                        {category === "Student Management" && <FaUsers />}
                        {category === "Financial Management" && <FaChartLine />}
                        {category === "Communication" && <FaComments />}
                        {category === "Analytics" && <FaClipboardList />}{" "}
                        {category}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="sidebar-widget recent-posts-widget">
                <h3>Recent Resources</h3>
                <ul>
                  {blogPosts.slice(0, 3).map((post) => (
                    <li key={post.id}>
                      <div className="recent-post-img">
                        <img src={post.image} alt={post.title} />
                      </div>
                      <div className="recent-post-info">
                        <h4>
                          <a href={`/blog/${post.id}`}>{post.title}</a>
                        </h4>
                        <span>{post.date}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="sidebar-widget tags-widget">
                <h3>Popular Topics</h3>
                <div className="tags-cloud">
                  {[...new Set(blogPosts.flatMap((post) => post.tags))].map(
                    (tag, index) => (
                      <a
                        key={index}
                        href={`/blog/tag/${tag.toLowerCase()}`}
                        className="tag"
                      >
                        <FaTag /> {tag}
                      </a>
                    )
                  )}
                </div>
              </div>

              <div className="sidebar-widget cta-widget">
                <h3>Need Help With Your School Management?</h3>
                <p>Get personalized advice for your educational institution</p>
                <a href="/contact" className="btn btn-primary">
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
